import { useEffect, useState } from 'react';

/**
 * Time-of-day greeting that stays correct while the app is open.
 *
 * The home screen used to print a hardcoded "Good morning," at every hour of the
 * day. Reading the clock once at mount is not enough either: this is an installed
 * PWA that agents leave on the home screen for days, so the component that
 * rendered "Good morning" at 09:00 is still mounted at 21:00.
 *
 * Rather than re-render every minute to catch a boundary that moves three times a
 * day, this schedules a single timeout at the next boundary and re-arms from
 * there. A backgrounded tab has its timers throttled or frozen, so waking the
 * page also re-evaluates immediately instead of waiting for a timer that may
 * never have fired.
 */

const BOUNDARIES = [
  { until: 12, text: 'Good morning' },
  { until: 18, text: 'Good afternoon' },
  { until: 24, text: 'Good evening' },
];

/** Exported for tests — the greeting for any given moment. */
export const greetingAt = (date = new Date()) =>
  BOUNDARIES.find((b) => date.getHours() < b.until).text;

export default function useGreeting() {
  const [greeting, setGreeting] = useState(greetingAt);

  useEffect(() => {
    let timer;

    const tick = () => {
      const now = new Date();
      setGreeting(greetingAt(now));
      // Next boundary in LOCAL time. setHours(24, …) rolls cleanly into tomorrow
      // midnight, so the evening slot re-arms without special-casing the day.
      const next = new Date(now);
      next.setHours(BOUNDARIES.find((b) => now.getHours() < b.until).until, 0, 0, 0);
      // Never schedule a zero-delay timer: at exactly 12:00:00.000 the next
      // boundary is now, and a 0ms re-arm would spin.
      timer = setTimeout(tick, Math.max(1000, next.getTime() - now.getTime()));
    };

    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      clearTimeout(timer);
      tick();
    };

    tick();
    document.addEventListener('visibilitychange', onWake);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, []);

  return greeting;
}
